import { ThreeBounce } from 'better-react-spinkit';
import { observer } from 'mobx-react-lite';
import React, {
    useCallback,
    useContext,
    useEffect,
    useState
} from 'react';
import { FaSpinner } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Button,
    Spinner
} from 'reactstrap';
import { DialogContext } from '../../store/context/DialogContext';
import { useTranslation } from '../../store/context/TranslationContext';
import { doGET } from '../../util/HttpUtil';
import CountrySelectService from '../CSC/CountrySelect/CountrySelectService';
import BPlusCart from './BPlusCart';
import { PAYMENT_RETURN_PARAM, paymentCopy, STATUS } from './CartService';
import EmbeddedCheckoutUI from './EmbeddedCheckoutUI';
import IPlusAddress from './IPlusAddress';
import IPlusCart from './IPlusCart';
import PaymentStatusCard from './PaymentStatusCard';
import './stripe.css';
import { buildAutomationChildTestId, buildAutomationValueTestId, resolveAutomationTestId } from '../InputField/testId';

const PayNow = ({ onSubmit, onBackClick, handleUpdateFormData, setHasError, data, shouldCollectAddress = false, testId }) => {
    const { showError } = useContext(DialogContext);
    const { t } = useTranslation();
    const [unpaidCartId, setUnpaidCartId] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState(STATUS.INIT);
    const [loading, setLoading] = useState(false);
    const [totalPaidAmount, setTotalPaidAmount] = useState(null);
    const [applicationFormData, setApplicationFormData] = useState({});
    const resolvedTestId = resolveAutomationTestId(testId, "payment");
    const location = useLocation();
    const navigate = useNavigate();

    const fetchApplicationFormData = async (editId) => {
        try {
            const applicationResponse = await doGET(`/api/application-form/detail?id=${editId}&withExtension=true`)
            if (applicationResponse?.status == 200) {
                setApplicationFormData(applicationResponse?.data)
            }
        } catch (error) {
            showError(error)
        }
    }

    const { COUNTRY_MASTER_KEYS } = CountrySelectService;

    const {
        SANCTION_COUNTRY: sanctionCountryKey,
        VISA_CARD: visaCardKey
    } = COUNTRY_MASTER_KEYS;

    useEffect(() => {
        const returnedTransactionId = new URLSearchParams(location.search).get(PAYMENT_RETURN_PARAM);
        if (returnedTransactionId) {
            syncReturnedPayment(returnedTransactionId);
        } else {
            initFlow();
        }
        fetchApplicationFormData(data?._id)
    }, [data?._id]);

    // Back from a Stripe redirect (Amazon Pay, crypto, …): sync the transaction before showing the cart.
    const syncReturnedPayment = async (transactionId) => {
        const params = new URLSearchParams(location.search);
        params.delete(PAYMENT_RETURN_PARAM);
        const search = params.toString();
        navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
        try {
            await doGET(`/api/transaction/update-status?id=${transactionId}`);
        } catch (err) {
            console.error('Payment Return Error', err);
        }
        initFlow();
    };


    const handlePayNow = useCallback(async (isSubmit) => {
        if (!unpaidCartId) return;
        setPaymentStatus(STATUS.INIT);
        try {
            if (isSubmit) {
                const res = await doGET(`/api/transaction/cart/pay?cart_id=${unpaidCartId}`);
                if (res?.data?.status == 2) {
                    handleSubmit();
                }
            } else {
                setPaymentStatus(STATUS.PAY_CART_AMOUNT);
            }
        } catch (err) {
            console.error('Start Payment Error', err);
            setPaymentStatus(STATUS.ERROR);
        }
    }, [unpaidCartId]);

    const initFlow = useCallback(async () => {
        try {
            const res = await doGET(`/api/cart/grid?application_id=${data._id}`);
            let allCarts = res.data.rows || [];

            if (!allCarts?.length) {
                const res = await doGET(`/api/cart/app/create?id=${data._id}`);
                allCarts = [res.data ?? {}];
            }

            if (allCarts?.length) {
                const unpaid = allCarts.find((c) => c.status == 0);
                const paidCarts = allCarts.filter((c) => c.status == 1);
                const totalPaid = paidCarts.reduce(
                    (sum, c) => sum + (Number(c.payable) || 0),
                    0
                );
                setTotalPaidAmount(totalPaid);

                if (!unpaid) {
                    setPaymentStatus(STATUS.COMPLETED);
                } else {
                    setUnpaidCartId(unpaid._id);
                    if (data?.formType == "INDIVIDUAL_PLUS") {
                        if (
                            CountrySelectService?.cyclosCountryMasterMap?.[data?.address?.country]?.[visaCardKey] == "No"
                            || CountrySelectService?.cyclosCountryMasterMap?.[data?.address?.country]?.[sanctionCountryKey] == "Yes"
                            || !shouldCollectAddress
                        ) {
                            setPaymentStatus(STATUS.MAKE_CART);
                        } else {
                            setPaymentStatus(STATUS.ADDRESS_COLLECTION);
                        }
                    } else if (["BUSINESS_PLUS", "BUSINESS"].includes(data?.formType)) {
                        setPaymentStatus(STATUS.MAKE_CART);
                    }
                }
            }


        } catch (err) {
            console.error('Init Flow Error', err);
            showError(err);
            setPaymentStatus(STATUS.ERROR);
        }
    }, [data._id]);

    const handleSubmit = async () => {
        setHasError(false);
        setLoading(true);
        try {
            await onSubmit();
        } catch (err) {
            console.error('Submit Error', err);
            setHasError(true);
        } finally {
            setLoading(false);
        }
    };

    // Simple loader
    const Loader = () => (
        <div className="fv_pay_now_loading" data-testid={buildAutomationValueTestId(buildAutomationChildTestId(resolvedTestId, "state"), "loading")}>
            <FaSpinner className="fa-spin" />
            <p>{t(paymentCopy.loading)}</p>
            <div className="fv_amount_due_full_screen_loader" data-testid={buildAutomationChildTestId(resolvedTestId, "loading-overlay")}>
                <Spinner color='#fff' size="xl" />
            </div>
        </div>
    );


    const renderContent = () => {
        switch (paymentStatus) {
            case STATUS.INIT:
                return <Loader />;
            case STATUS.ADDRESS_COLLECTION:
                return <IPlusAddress
                    testId={buildAutomationChildTestId(resolvedTestId, "shipping-address")}
                    data={data}
                    applicationId={data._id}
                    onContinue={() => { setPaymentStatus(STATUS.MAKE_CART) }}
                    onBackClick={onBackClick}
                    handleUpdateFormData={handleUpdateFormData}
                />;
            case STATUS.MAKE_CART:
                switch (data?.formType) {
                    case "INDIVIDUAL_PLUS":
                        return <IPlusCart
                            testId={buildAutomationChildTestId(resolvedTestId, "cart")}
                            data={data}
                            applicationId={data._id}
                            cartId={unpaidCartId}
                            onPayNow={handlePayNow}
                            paymentStatus={paymentStatus}
                            applicationFormData={applicationFormData}
                            totalPaidAmount={totalPaidAmount}
                            handleUpdateFormData={handleUpdateFormData}
                            onBackClick={() => {
                                if (
                                    CountrySelectService?.cyclosCountryMasterMap?.[data?.address?.country]?.[visaCardKey] == "No"
                                    || CountrySelectService?.cyclosCountryMasterMap?.[data?.address?.country]?.[sanctionCountryKey] == "Yes"
                                    || !shouldCollectAddress
                                ) {
                                    onBackClick();
                                } else {
                                    setPaymentStatus(STATUS.ADDRESS_COLLECTION);
                                }
                            }}
                        />;
                    case "BUSINESS_PLUS":
                    case "BUSINESS":
                        return <BPlusCart
                            data={data}
                            applicationId={data._id}
                            cartId={unpaidCartId}
                            onPayNow={handlePayNow}
                            paymentStatus={paymentStatus}
                            applicationFormData={applicationFormData}
                            totalPaidAmount={totalPaidAmount}
                            handleUpdateFormData={handleUpdateFormData}
                            onBackClick={onBackClick}
                        />
                    default:
                        return <Loader />;
                }

            case STATUS.PAY_CART_AMOUNT:
                return <EmbeddedCheckoutUI
                    testId={buildAutomationChildTestId(resolvedTestId, "checkout")}
                    unpaidCartId={unpaidCartId}
                    onBackClick={() => {
                        setPaymentStatus(STATUS.MAKE_CART)
                    }}
                    onPaymentComplete={() => {
                        setPaymentStatus(STATUS.COMPLETED);
                        initFlow();
                    }}
                    onError={() => {
                        setPaymentStatus(STATUS.ERROR);
                    }}
                />;

            case STATUS.COMPLETED:
                return <PaymentStatusCard variant="success" testId={buildAutomationValueTestId(buildAutomationChildTestId(resolvedTestId, "state"), "completed")} />
            case STATUS.ERROR:
                return <PaymentStatusCard variant="error" onRetry={initFlow} testId={buildAutomationValueTestId(buildAutomationChildTestId(resolvedTestId, "state"), "error")} />;
            default:
                return <Loader />;
        }
    };

    const shouldShowMainBackButton = () => {
        if (
            [
                STATUS.ADDRESS_COLLECTION,
                STATUS.PAY_CART_AMOUNT,
                STATUS.MAKE_CART
            ]?.includes(paymentStatus)
        ) {
            return false;
        }
        return true;
    }

    return (
        <div className="fv_pay_now_container" data-testid={resolvedTestId} data-record-id={data?._id}>
            {loading && <div className="fv_amount_due_full_screen_loader" data-testid={buildAutomationChildTestId(resolvedTestId, "submit-loading-overlay")}>
                <Spinner color='#fff' size="xl" />
            </div>}
            <div className="fv_pay_now_content" data-testid={buildAutomationChildTestId(resolvedTestId, "content")}>
                {renderContent()}
                <div className="fv_pay_now_footer" data-testid={buildAutomationChildTestId(resolvedTestId, "footer")}>
                    {shouldShowMainBackButton() && <Button
                        data-testid={buildAutomationChildTestId(resolvedTestId, "back")}
                        className='form-content-btn-outline mt-5'
                        disabled={loading}
                        onClick={() => {
                            onBackClick();
                        }}
                    >
                        {loading ? <ThreeBounce color="#000" /> : t("Back")}
                    </Button>}

                    {paymentStatus === STATUS.COMPLETED && (
                        <Button
                            data-testid={buildAutomationChildTestId(resolvedTestId, "submit-application")}
                            className={`form-content-btn mt-5`}
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading
                                ? <ThreeBounce color="white" size={8} />
                                : t(paymentCopy.submit_application)
                            }
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default observer(PayNow);
